import { definitions, operations, paths } from '@/apitypes';
import path from 'path/posix';
import { ReactDatePickerProps } from 'react-datepicker';

export type Company = operations['CompanyQuery_Get']['responses']['200']['schema']['Results'][0];

export type CompanyBooking =
    operations['BookingQuery_Get']['responses']['200']['schema']['Results'][0];

// NOTE: superadmin endpoints are not currently part of the generated OpenAPI types.
// Keep these as loose types to avoid breaking the build.
export type CompanyAdministrator = any;

export type CompanyLicense = any;

export type CompanyInvoice = definitions['CompanyInvoiceQueryResponse'];

export type InitialReducerState<T> = {
    entity: T;
    loading: boolean;
    error: any;
};

export type SyncMailchimpPutQuery = {
    DeleteNonExisting?: boolean;
    Test?: boolean;
    MemberCompanyId?: string;
};

export type CreateCompanyComment = {
    CompanyId: string;
    Comment: string;
    CommentType: string;
};
export interface CompanyComment {
    Id: string;
    CompanyId: string;
    Comment: string;
    CommentType: string;
    Updated: string;
    Created: string;
}

export type CommentType = {
    Id: number;
    Name: string;
    Description: string;
};

// NOTE: superadmin incentive endpoints are not in the generated types
export type PostIncentive = any;
export type PostIncentiveResponse = any;
export type Incentive = PostIncentiveResponse;
export type GetIncentivesQuery = any;
export type GetIncentivesResponse = any;

export type GetLicenseTypesQuery = paths['/licenses/types/']['get']['parameters']['query'];
export type GetLicenseTypesResponse =
    paths['/licenses/types/']['get']['responses']['200']['schema'];

export type LicenseTerminationTiming =
    | 'Immediately'
    | 'AtSubscriptionBillingPeriodEnd'
    | 'AtBindingPeriodEnd';

export type EndCompanyLicensesPayload = {
    CompanyId: string;
    EndDate?: string | null;
    CloseCompany?: boolean;
    Type?: LicenseTerminationTiming;
};

export interface PostInvoiceJobQuery {
    /**
     * A list of company IDs to generate invoices for. If omitted, all companies will be processed.
     */
    companyIds?: string[];
    /**
     * Controls up to which point of time transactions are included in invoice, can be set to any date, if not provided current UTC time
     * will be used instead
     */
    InvoiceDate?: string;
    /**
     * If present the operation will start only after this date. |
     */
    ScheduledStartTime?: string;
    /**
     * Optional CRON expression for recurring operations. |
     */
    RecurringSchedule?: string;
    /**
     * Only usage will be invoiced
     */
    OnlyUsage?: boolean;
}

export enum IncentiveActionType {
    'Upgrade' = 1,
    'AddOn' = 2,
    'Information' = 3,
}

export type NestedKey<O extends Record<string, unknown>> = {
    [K in Extract<keyof O, string>]: O[K] extends Array<any>
        ? K
        : O[K] extends Record<string, unknown>
        ? `${K}` | `${K}.${NestedKey<O[K]>}`
        : K;
}[Extract<keyof O, string>];

export interface SelectOption {
    value: number;
    label: string;
}

export enum LicensePlanId {
    Monthly = 1,
    Quarterly = 2,
    Yearly = 3,
}

export type ActionsResponse = {
    /** Format: int32 */
    Offset?: number;
    /** Format: int32 */
    Total?: number;
    Results?: any[];
    Meta?: definitions['Dictionary_String_String_'];
    ResponseStatus?: definitions['ResponseStatus'];
};

export type GetVossInvoiceBalanceQuery =
    paths['/voss/invoice/balance']['get']['parameters']['query'];
export type GetVossInvoiceBalanceResponse =
    paths['/voss/invoice/balance']['get']['responses']['200']['schema'];

export type GetVossSingleProductGroupQuery =
    operations['GetVossProductGroupproductGroupsProductGroupId_Get']['parameters']['query'];
export type GetSingleVossProductGroupResponse =
    operations['GetVossProductGroupproductGroupsProductGroupId_Get']['responses']['200']['schema'];
export type GetVossProductGroupsQuery =
    operations['GetVossProductGroupsproductGroups_Get']['parameters']['query'];
export type GetVossProductGroupsResponse =
    operations['GetVossProductGroupsproductGroups_Get']['responses']['200']['schema'];
export type GetVossProductsQuery = operations['GetVossProductsproducts_Get']['parameters']['query'];
export type GetVossProductsResponse =
    operations['GetVossProductsproducts_Get']['responses']['200']['schema'];

export enum ProductType {
    Main,
    Addon,
    License,
    Usage,
}

export enum DiscountType {
    FlatPrice,
    Percentage,
}

// DiscountAgreement
export interface PostDiscountAgreement {
    Name: string;
    Description: string;
    ValidFrom: string;
    DiscountPeriod: DiscountPeriod;
    ChargePlanDiscounts: ChargePlanDiscounts;
}

export interface DiscountPeriod {
    Length: Length;
}

export interface Length {
    Unit: number;
    Value: number;
}

export interface ChargePlanDiscounts {
    ChargePlanId: string;
    ProductGroupDiscounts: ProductGroupDiscounts;
}

export interface ProductGroupDiscounts {
    ProductGroupId: string;
    ProductPricingDiscounts: ProductPricingDiscounts;
}

export interface ProductPricingDiscounts {
    ProductId: string;
    DiscountType: number;
    UnitPriceDiscount: number;
}

export enum DiscountLengthUnit {
    Day,
    Month,
    Year,
}

export interface Option {
    value: string | number;
    label: string;
}

export type GrayFormikProps =
    | {
          type: 'radio';
          options?: Option[];
          values?: { [key in string]: any };
      }
    | {
          type: 'select';
          options?: Option[];
          values?: never;
      }
    | {
          // default text input
          type?: 'text' | 'email' | 'string';
          options?: never;
          values?: never;
      }
    | {
          type: 'textarea';
          options?: never;
          values?: never;
      }
    | {
          type: 'array';
          options?: never;
          values?: never;
      }
    | {
          type: 'checkbox';
          options?: never;
          values?: never;
      }
    | {
          type: 'dropdownsearch';
          items: { value: string; label: string }[];
          onSearchChange?: (val: string) => void;
          onSelect?: (item: any) => void;
          options?: never;
          values?: never;
          total?: number;
      }
    | {
          type: 'dropdowncombobox';
          options?: never;
          values?: never;
      }
    | {
          type: 'number';
          options?: never;
          values?: never;
      }
    | {
          type: 'datetime';
          variation?: 'normal' | 'compact';
          field: {
              name: string;
              value: Date | null;
          };
          onChange: ReactDatePickerProps['onChange'];
          options?: never;
          values?: never;
      };

export type GeneralFieldProps = {
    as?: HTMLElement | React.ReactNode;
    label?: string;
    disabled?: boolean;
    required?: boolean;
    isLoading?: boolean;
    placeholder?: string;
    prefix?: string;
    suffix?: string;
    autoComplete?: string;
};

export type Subscription = {
    id: string;
    customer: {
        id: string;
        name: string;
        customerNumber: number;
        organizationNumber: string;
    };
    package: {
        id: string;
        versionId: string;
        name: string;
    };
    tier: {
        id: string;
        name: string;
        externalId: string;
    };
    plan: {
        id: string;
        name: string;
    };
    productGroups: Array<{
        productGroupType: string;
        id: string;
        name: string;
        externalId: string;
        products: Array<{
            type: string;
            id: string;
            name: string;
        }>;
    }>;
    bindingPeriod: {
        periodKind: string;
        id: string;
        startDate: string;
        endDate: string;
    };
    subscriptionBillingPeriod: {
        periodKind: string;
        id: string;
        startDate: string;
        endDate: string;
    };
    usageBillingPeriod: {
        periodKind: string;
        id: string;
        startDate: string;
        endDate: string;
    };
    created: string;
    externalId: string;
    status?: 'Active' | 'Terminated' | string;
    termination?: {
        timing: string;
        scheduledDate: string;
    } | null;
};

export enum DiscountAgreementStatus {
    Draft,
    Staged,
    Published,
    Archived,
    Discarded,
}

export { DiscountAgreementStatus as DiscountAgreementStatusEnum };

export interface DiscountAgreementListItem {
    Id: string;
    VersionId: string;
    SourceVersionId?: string | null;
    PackageId: string;
    ExternalId?: string | null;
    Status: string;
    StatusId: number;
    Name: string;
    Description: string;
    ValidFrom: string;
    ValidTo?: string | null;
    DiscountPeriod?: DiscountAgreementPeriod | null;
}

export interface DiscountAgreement extends DiscountAgreementListItem {
    ApplicationScope: string;
    ApplicationScopeId: number;
    ApplicationPeriodKind: string;
    ApplicationPeriodKindId: number;
    AutomaticApplicationKind: string;
    AutomaticApplicationKindId: number;
    Package: Package;
    Customers: any[];
    CustomerGroups: any[];
    ChargePlanDiscounts?: ChargePlanDiscount[] | null;
}

export type PutDiscontAgreementResponse = DiscountAgreement;

export interface ChargePlanDiscount {
    Id: string;
    ChargePlanId: string;
    ProductGroupDiscounts?: ProductGroupDiscount[] | null;
}

export interface ProductGroupDiscount {
    Id: string;
    ProductGroupId: string;
    ProductPricingDiscounts?: ProductPricingDiscount[] | null;
}

export interface ProductPricingDiscount {
    Id: string;
    ProductId: string;
    DiscountType: string;
    DiscountTypeId: number;
    UnitPriceDiscount: number;
    TierPricingDiscounts: any[];
}

export interface DiscountAgreementPeriod {
    PeriodKind: string;
    PeriodKindId: number;
    Length?: DiscountAgreementTimeLength | null;
    PeriodIterationCount?: number | null;
}

export interface DiscountAgreementTimeLength {
    Unit: string;
    UnitId: number;
    Value: number;
}

export interface Package {
    id: string;
    name: string;
}

export type MailchimpSyncExecutionsQuery = {
    CreatedDate?: string | null; // ISO string, date-time (null when no date filter)
    CompanyId?: string; // Guid as string
};

export type MailchimpSyncExecution = {
    MailchimpSyncExecutionId: string;
    SuccessCount: number;
    FailedCompanyIds: string[];
    Status: string;
    Manual: boolean;
    CompanyId: string;
    Errors: string[];
    CreatedDate: string;
};

export type WidgetConfigurationSchemaQuery =
    operations['WidgetConfigurationSchemaQueryconfigurationschema_Get']['parameters']['query'];
export type GetWidgetConfigurationSchemasResponse = Omit<
    operations['WidgetConfigurationSchemaQueryconfigurationschema_Get']['responses']['200']['schema'],
    'Results'
> & {
    Results?: WidgetConfigurationSchemaResponse[];
};
export type WidgetConfigurationSchemaResponse = definitions['WidgetConfigurationSchemaResponse'] & {
    // apitypes.ts is stale: the generated definition omits this field even though
    // legacy-api's WidgetConfigurationSchemaResponse DTO always serializes it.
    MigrationFromPreviousVersion?: string;
    DefaultConfiguration?: string;
};
export type CreateWidgetConfigurationSchemaBody = definitions['CreateWidgetConfigurationSchema'] & {
    // apitypes.ts is stale: the generated definition omits this field even though
    // legacy-api's CreateWidgetConfigurationSchema DTO accepts it.
    DefaultConfiguration?: string;
};

// Widget Configuration instances (company-scoped)

export type WidgetConfigurationResponse = definitions['WidgetConfigurationResponse'] & {
    // apitypes.ts is stale: the generated definition omits this field even though
    // legacy-api's WidgetConfigurationResponse DTO always serializes it.
    IsHomepageDefault?: boolean;
};

export type GetWidgetConfigurationsQuery =
    operations['WidgetConfigurationQueryconfiguration_Get']['parameters']['query'];

export type GetWidgetConfigurationsResponse = Omit<
    definitions['QueryResponse_WidgetConfigurationResponse_'],
    'Results'
> & {
    Results?: WidgetConfigurationResponse[];
};

export type WidgetConfigurationByIdParams = definitions['WidgetConfigurationByIdQuery'];

export type CreateWidgetConfigurationBody = definitions['CreateWidgetConfiguration'];

export type UpdateWidgetConfigurationBody = definitions['UpdateWidgetConfiguration'];

export type DeleteWidgetConfigurationParams = definitions['DeleteWidgetConfiguration'];

// Reset / bump-version: not in generated swagger yet (legacy-api added these after the
// last `yarn gen:types` run). Hand-written to match WidgetConfigurationDtos.cs exactly.

export type ResetWidgetConfigurationParams = {
    CompanyId?: string;
    Id: string;
};

export type BumpWidgetConfigurationVersionBody = {
    CompanyId?: string;
    Id: string;
    UseFactoryDefaults: boolean;
};

// Widget Configuration Schema migration patch: also not in generated swagger yet.

export type SetWidgetConfigurationSchemaMigrationBody = {
    Version: number;
    Patch: string;
};

export type DeleteWidgetConfigurationSchemaMigrationParams = {
    Version: number;
};

// Tickets (Support Cases)
// The SuperAdmin request DTOs are not in the generated OpenAPI types; defined manually here.
// Response shapes come from the generated types.

export type Ticket = definitions['SupportCaseQueryResponse'];
export type TicketsResponse = definitions['QueryResponse_SupportCaseQueryResponse_'];

export type TicketStatus = definitions['SupportCaseStatusResponse'];
export type TicketStatusesResponse = definitions['QueryResponse_SupportCaseStatusResponse_'];

export type TicketType = definitions['SupportCaseTypeResponse'];
export type TicketTypesResponse = definitions['QueryResponse_SupportCaseTypeResponse_'];

export type TicketArea = definitions['SupportCaseAreaResponse'];
export type TicketAreasResponse = definitions['QueryResponse_SupportCaseAreaResponse_'];

export type TicketComment = definitions['SupportCaseCommentsResponse'];
export type TicketCommentsResponse = definitions['QueryResponse_SupportCaseCommentsResponse_'];

export type GetTicketsQuery = {
    CompanyId?: string;
    Active?: boolean;
    Id?: number;
    Skip?: number;
    Take?: number;
    OrderBy?: string;
    OrderByDesc?: string;
    IncludeCaseComments?: boolean;
    IncludeCaseAttachments?: boolean;
    IncludeCaseStatusInformation?: boolean;
    IncludeCaseTypeInformation?: boolean;
    IncludeCaseAreaInformation?: boolean;
    IncludeCaseStatusOptions?: boolean;
    IncludeCaseTypeOptions?: boolean;
    IncludeCaseAreaOptions?: boolean;
};

export type CreateTicket = {
    CompanyId?: string;
    Title: string;
    Description: string;
    CaseTypeId: number;
    CaseAreaId: number;
};

export type UpdateTicket = {
    Id: number;
    CompanyId?: string;
    Title?: string;
    Description?: string;
    CaseTypeId?: number;
    CaseAreaId?: number;
    CaseStatusId?: number;
};

export type DeleteTicketParams = {
    Id: number;
    CompanyId?: string;
};

export type CreateTicketComment = {
    Id: number; // support case id (path param)
    CompanyId?: string;
    Comment: string;
};

export type DeleteTicketCommentParams = {
    SupportCaseId: number;
    Id: number; // comment id
    CompanyId?: string;
};

// SuperAdmin Customers
// The /superadmin/customers request/response DTOs aren't in apitypes.ts yet (same as
// tickets). Re-run `yarn gen:types` once the backend OpenAPI is
// published to replace these with the generated definitions.

export type SuperAdminCustomerMatch = {
    CompanyId: string; // Guid; the empty GUID for profile-only rows
    CompanyName?: string;
    CustomerId: string; // Guid (RegisteredUserId, or user profile id for profile-only rows) — path {Id} for delete
    Firstname?: string;
    Lastname?: string;
    Email?: string;
    Phone?: string;
    ActiveBookingsCount?: number;
    TotalBookingsCount?: number;
    IsProfileOnly?: boolean; // user profile with no customer registration in any company
};

export type CustomersResponse = {
    Offset?: number;
    Total?: number;
    Results?: SuperAdminCustomerMatch[];
};

export type GetCustomersQuery = {
    Email?: string;
    Skip?: number;
    Take?: number;
    OrderBy?: string;
    OrderByDesc?: string;
};

export type DeleteCustomerParams = {
    Id: string; // CustomerId (RegisteredUserId), or user profile id for profile-only rows
    CompanyId: string;
    // When false, the backend keeps the shared user profile + Keycloak login even if this
    // was the customer's last registration. Omitted (backend default) means true.
    DeleteUserProfileAndIdentity?: boolean;
};

export type DeletedCompanyResponse = {
    CompanyId?: string;
    Comment?: string;
    VossCustomerId?: string | null;
    VossCustomerNote?: string | null;
    DeletionScheduleId?: number | null;
};

// Company deletion schedule (mark for deletion)
// SuperAdmin request DTOs aren't in apitypes.ts (same as tickets); shapes mirror
// CompanyDeletionScheduleDtos.cs on the backend.

export const COMPANY_DELETION_SCHEDULE_STATUS = {
    Scheduled: 1,
    Cancelled: 2,
    Deleted: 3,
    Failed: 4,
} as const;

export interface ApproveCompanyPayload {
    CompanyId: string;
    Reason: string;
    CancelScheduledDeletion: boolean;
    SetApprovedByAdmin: boolean;
}

export interface ApproveCompanyResponse {
    CompanyId: string;
    PreviousStatusId: number;
    StatusId: number;
    DeletionScheduleCancelled: boolean;
    Warnings: string[];
}

export const COMPANY_STATUS = { Approved: 3, Inactive: 4, Closed: 5, NotApproved: 6 } as const;

export type CompanyDeletionScheduleStatusName = keyof typeof COMPANY_DELETION_SCHEDULE_STATUS;

export type CompanyDeletionScheduleMetaData = {
    LicenseTerminationTiming?: LicenseTerminationTiming | null;
    CloseCompanyNow?: boolean;
    NotifyCompany?: boolean;
    ActiveLicenseIds?: number[];
    LicensesValidTo?: string | null;
    InactivityNotificationDate?: string | null;
    BookingCount?: number | null;
};

export type CompanyDeletionScheduleResponse = {
    Id: number;
    CompanyId: string;
    CompanyName?: string | null;
    Status: number;
    StatusName: CompanyDeletionScheduleStatusName | string;
    Source: number;
    SourceName: string;
    ScheduledDeletionDate?: string | null;
    Reason?: string | null;
    MetaData?: CompanyDeletionScheduleMetaData;
    VossCustomerId?: string | null;
    VossCustomerNote?: string | null;
    CreatedBy?: string | null;
    CreatedDate: string;
    UpdatedDate?: string | null;
    CancelledBy?: string | null;
    CancelledDate?: string | null;
    CancelReason?: string | null;
    ProcessedDate?: string | null;
    ErrorMessage?: string | null;
    CompanyExists: boolean;
    Warnings: string[];
};

export type CompanyDeletionSchedulesResponse = {
    Offset: number;
    Total: number;
    Results: CompanyDeletionScheduleResponse[];
};

export type CompanyDeletionScheduleQuery = {
    CompanyId?: string;
    Status?: number;
    Statuses?: number[];
    Source?: number;
    Take?: number;
    Skip?: number;
    OrderByDesc?: string;
};

export type ScheduleCompanyDeletionPayload = {
    CompanyId: string;
    Reason: string;
    LicenseTerminationTiming?: LicenseTerminationTiming | null;
    CloseCompanyNow?: boolean;
    NotifyCompany?: boolean;
    ScheduledDeletionDate?: string | null;
    AcknowledgeInactivityWarning?: boolean;
};

export type CancelCompanyDeletionPayload = {
    CompanyId: string;
    CancelReason: string;
};

// Newsletter broadcast (superadmin)
// SuperAdmin request/response DTOs aren't in apitypes.ts (same as tickets).

export type NewsletterBroadcastPayload = {
    CompanyStatusIds: number[];
    /** Include each matching company's active admins (ApplicationUsers rows) as recipients. */
    IncludeCompanyOwners: boolean;
    /** Include each matching company's own contact email (CompanyEmail) as a recipient. */
    IncludeCompanyEmails: boolean;
    Subject: string;
    HtmlBody: string;
    StartSendingAt?: string;
    /** Required system/service notices bypass the newsletter unsubscribe list. Superadmin-only. */
    IsSystemNotice?: boolean;
};

export type NewsletterBroadcastResponse = {
    TotalRecipients: number;
    TotalBatches: number;
    TotalUnsubscribed: number;
    FirstSendDate?: string | null;
    LastSendDate?: string | null;
};

export type TestNewsletterBroadcastPayload = {
    Subject: string;
    HtmlBody: string;
    TestRecipientEmail: string;
    IsSystemNotice?: boolean;
};

export type CompanyFeatureFlagResponse = definitions['CompanyFeatureFlagResponse'];
export type GetCompanyFeatureFlagsResponse =
    definitions['QueryResponse_CompanyFeatureFlagResponse_'];
export type SetCompanyFeatureFlagBody = definitions['SetCompanyFeatureFlag'];

export type FeatureFlagAdminResponse = definitions['FeatureFlagAdminResponse'];
export type GetFeatureFlagsResponse = definitions['QueryResponse_FeatureFlagAdminResponse_'];

export type GetFeatureFlagsQuery = {
    Skip?: number;
    Take?: number;
    OrderBy?: string;
};

export type BulkSetCompanyFeatureFlagBody = {
    Id: number;
    Overrides: { CompanyId: string; IsEnabled: boolean }[];
};

export type BulkSetCompanyFeatureFlagResponse = {
    FeatureFlagId: number;
    CreatedCount: number;
    UpdatedCount: number;
};

export type CreateFeatureFlagBody = {
    Name: string;
    IsActive?: boolean;
    IsPublic?: boolean;
    CompanyAdminCanToggle?: boolean;
};

export type UpdateFeatureFlagBody = {
    Id: number;
    Name?: string | null;
    IsActive?: boolean | null;
    IsPublic?: boolean | null;
    CompanyAdminCanToggle?: boolean | null;
};

export type DeleteFeatureFlagParams = {
    Id: number;
};

export type ResetDemoAccountsResponse = {
    Success: boolean;
    Message: string;
};

export type AllowedRedirectDomainResponse = {
    Id: number;
    CompanyId: string;
    CompanyName?: string | null;
    Domain: string;
    CreatedDate: string;
    ResponseStatus?: unknown;
};

export type GetRedirectDomainsResponse = {
    Offset?: number;
    Total?: number;
    Results?: AllowedRedirectDomainResponse[];
};

export type GetRedirectDomainsQuery = {
    CompanyId?: string;
    Skip?: number;
    Take?: number;
    OrderBy?: string;
};

export type CreateRedirectDomainBody = {
    CompanyId: string;
    Domain: string;
};

export type DeleteRedirectDomainParams = {
    Id: number;
};

export type AssistantUsageMeter = {
    TokensUsed: number;
    TokenLimit: number;
    TokensRemaining: number;
    PercentUsed: number;
    IsWithinBudget: boolean;
};

export type SuperAdminAssistantUsage = {
    CompanyId: string;
    CompanyName: string;
    AiChat: AssistantUsageMeter | null;
    Mcp: AssistantUsageMeter | null;
};

export type GetAssistantUsageResponse = {
    Offset: number;
    Total: number;
    WindowDays: number;
    Results: SuperAdminAssistantUsage[];
};

export type GetAssistantUsageQuery = {
    Search?: string;
    CompanyId?: string;
    Skip?: number;
    Take?: number;
};

export type BillingInformation = {
    CompanyId: string;
    LicensePlanId: number;
    BillingMethodId: number;
    Name: string;
    FirstName: string;
    LastName: string;
    PhoneNumber?: string | null;
    ContactEmail?: string | null;
    Attention?: string | null;
    Street1?: string | null;
    Street2?: string | null;
    ZipCode: string;
    City: string;
    CountryId: string;
    Email?: string | null;
    GLN?: string | null;
    ReferenceLine1?: string | null;
    ReferenceLine2?: string | null;
    PaymentTermsDays: number;
    VatRegistrationNumber?: string | null;
};

export type UpdateBillingInformationPayload = BillingInformation;

export type BillingMethodOption = {
    Id: number;
    Name: string;
    Description?: string;
    Countries?: string[];
};

export type BillingCountryOption = {
    Id: string;
    Name?: string;
};

export type BillingLicensePlanOption = {
    Id: number;
    Name?: string;
    Active?: boolean;
};

export type QueryResults<T> = {
    Offset?: number;
    Total?: number;
    Results?: T[];
};

export type UsageAuditGranularity = 'Day' | 'Week' | 'Month' | 'Quarter';
export type UsageAuditSeverity = 'Ok' | 'Minor' | 'Major' | 'Critical';
export type UsageAuditUsageType = 'BookingUsage' | 'SmsUsage';
export type UsageAuditFindingKind =
    | 'ReportedButMissingInVoss'
    | 'VossOrphan'
    | 'QuantityMismatch'
    | 'TerminatedInVoss'
    | 'FailedLocally'
    | 'PendingStale'
    | 'RecountDrift'
    | 'PeriodShifted'
    | 'DuplicateInVoss'
    | 'RecountWithoutLocalRow'
    | 'UnverifiedInVoss';

export type GetVossUsageAuditQuery = {
    From?: string;
    To?: string;
    Granularity?: UsageAuditGranularity;
    CompanyId?: string;
    UsageType?: UsageAuditUsageType;
    SkipRecount?: boolean;
    OnlyDifferences?: boolean;
    MinorThresholdPercent?: number;
    Skip?: number;
    Take?: number;
};

export type UsageAuditFinding = {
    Kind: UsageAuditFindingKind;
    Severity: UsageAuditSeverity;
    Day: string;
    LocalReportId?: number;
    IdempotencyKey?: string;
    VossUsageId?: string;
    LocalQuantity?: number;
    VossQuantity?: number;
    RecountQuantity?: number;
    Note?: string;
};

export type UsageAuditBucket = {
    BucketStart: string;
    BucketEnd: string;
    Label: string;
    UsageType: UsageAuditUsageType;
    Recount?: number;
    Recorded: number;
    RecordedReported: number;
    RecordedPending: number;
    RecordedFailed: number;
    Voss: number;
    Delta: number;
    DeltaPercent: number;
    Severity: UsageAuditSeverity;
    MissingInVoss: number;
    OrphansInVoss: number;
    TerminatedInVoss: number;
};

export type UsageAuditCompanyRow = {
    CompanyId: string;
    CompanyName: string;
    VossCustomerId?: string;
    UsageType: UsageAuditUsageType;
    Recount?: number;
    Recorded: number;
    Voss: number;
    Delta: number;
    DeltaPercent: number;
    Severity: UsageAuditSeverity;
    WorstBucketLabel?: string;
    Findings: UsageAuditFinding[];
};

export type UsageAuditSummary = {
    From: string;
    To: string;
    Granularity: UsageAuditGranularity;
    MinorThresholdPercent: number;
    RecountTotal?: number;
    RecordedTotal: number;
    RecordedReported: number;
    RecordedPending: number;
    RecordedFailed: number;
    VossTotal: number;
    VossTerminated: number;
    Delta: number;
    DeltaPercent: number;
    CompaniesChecked: number;
    CompaniesWithDifferences: number;
    OkCompanies: number;
    MinorCompanies: number;
    MajorCompanies: number;
    CriticalCompanies: number;
    VossPagesFetched: number;
    Truncated: boolean;
    Notes: string[];
    GeneratedAtUtc: string;
};

export type VossUsageAuditResponse = {
    Summary: UsageAuditSummary;
    Buckets: UsageAuditBucket[];
    Companies: UsageAuditCompanyRow[];
    TotalCompanies: number;
};

export type VossLicenseAuditRunMode = 'Preview' | 'Apply';
export type VossLicenseAuditRunStatus = 'Running' | 'Completed' | 'Aborted' | 'Failed' | 'Stale';
export type VossLicenseAuditFindingKind =
    | 'TierMismatch'
    | 'SubscriptionMissing'
    | 'CustomerMissing'
    | 'SubscriptionTerminated'
    | 'AddOnTerminated'
    | 'AddOnQuantityMismatch'
    | 'AddOnMissingInVoss'
    | 'FailedToCheck'
    | 'SubscriptionReplaced'
    | 'AddOnMissingLocally'
    | 'ValidToMismatch'
    | 'PlanMismatch'
    | 'DuplicateActiveLicense'
    | 'BillingInformationChange'
    | 'ChangeLogRowUnresolved'
    | 'FreeNoSubscription';
export type VossLicenseAuditDecisionAction =
    | 'ReportOnly'
    | 'Applied'
    | 'WouldApply'
    | 'SkippedLocalChangePending'
    | 'SkippedPendingScheduledChange'
    | 'SkippedCap';

export type VossLicenseAuditRunSummary = {
    CompaniesChecked: number;
    Matched: number;
    TierMismatches: number;
    SubscriptionsMissing: number;
    CustomersMissing: number;
    FreeNoSubscription: number;
    SkippedPendingChanges: number;
    SkippedLocalChangePending: number;
    SubscriptionsTerminated: number;
    TiersChangedToVoss: number;
    AddOnsMatched: number;
    AddOnsTerminated: number;
    AddOnMismatches: number;
    SubscriptionsRelinked?: number;
    SubscriptionsAmbiguous?: number;
    AddOnsCreated?: number;
    ValidToSynced?: number;
    PlansSynced?: number;
    DuplicatesEnded?: number;
    ChangeLogRowsPreviewed?: number;
    BillingUpdates?: number;
    ChangeLogRowsUnresolved?: number;
    MutationsApplied: number;
    MutationsSkippedByCap: number;
    ErrorLogRowsWritten: number;
    ErrorLogRowsSkippedAsDuplicate: number;
    FailedToCheck: number;
    Aborted: boolean;
    FailureReason?: string;
};

export type CancelVossLicenseAuditRunResponse = {
    Cancelled: boolean;
};

export type VossLicenseAuditRunItem = {
    Id: number;
    Mode: VossLicenseAuditRunMode;
    Status: VossLicenseAuditRunStatus;
    TriggeredBy?: string;
    MaxMutations: number;
    CompaniesTotal: number;
    CompaniesChecked: number;
    Summary?: VossLicenseAuditRunSummary;
    StartedDate: string;
    HeartbeatDate: string;
    FinishedDate?: string;
};

export type VossLicenseAuditFindingItem = {
    Id: number;
    CompanyId: string;
    CompanyName?: string;
    Kind: VossLicenseAuditFindingKind;
    Decision: VossLicenseAuditDecisionAction;
    LicenseTypeId?: number;
    LicenseTypeName?: string;
    VossTierExternalId?: string;
    VossTierName?: string;
    AddOnLicenseTypeId?: number;
    AddOnLicenseTypeName?: string;
    LocalQuantity?: number;
    VossQuantity?: number;
    VossSubscriptionId?: string;
    Note?: string;
    CreatedDate: string;
};

export type GetVossLicenseAuditRunsQuery = {
    Take?: number;
};

export type VossLicenseAuditRunsResponse = {
    Runs: VossLicenseAuditRunItem[];
};

export type GetVossLicenseAuditFindingsQuery = {
    RunId?: number;
    Kind?: VossLicenseAuditFindingKind;
    Decision?: VossLicenseAuditDecisionAction;
    ExcludeReportOnly?: boolean;
    CompanyId?: string;
    Skip?: number;
    Take?: number;
};

export type VossLicenseAuditFindingsResponse = {
    Run?: VossLicenseAuditRunItem;
    Items: VossLicenseAuditFindingItem[];
    Total: number;
};

export type StartVossLicenseAuditPreviewPayload = {
    MaxMutations?: number;
};

export type StartVossLicenseAuditPreviewResponse = {
    RunId?: number;
    Queued: boolean;
    AlreadyRunning: boolean;
};

export type CompanyService = definitions['ServiceQueryResponse'];
export type CompanyServicesResponse = definitions['QueryResponse_ServiceQueryResponse_'];
export type GetCompanyServicesQuery = {
    CompanyId: string;
    Active: true;
    Skip?: number;
    Take?: number;
    OrderBy?: string;
};

export type CompanyServicePrice = definitions['ServicePriceResponse'];
export type CompanyServicePricesResponse = definitions['QueryResponse_ServicePriceResponse_'];
export type GetCompanyServicePricesQuery = {
    CompanyId: string;
    Active: true;
};

export type CompanyArticle = definitions['ArticleResponse'];
export type CompanyArticlesResponse = definitions['QueryResponse_ArticleResponse_'];
export type GetCompanyArticlesQuery = {
    CompanyId: string;
    IncludeServiceInformation?: boolean;
    Skip?: number;
    Take?: number;
    OrderBy?: string;
};

export type ArticleType = definitions['ArticleTypesQueryResponse'];
export type ArticleTypesResponse = QueryResults<ArticleType>;
